/**
 * DATA STALE banner — FR-A8 · plan §6.7.
 *
 * High-contrast status region. Sets `[data-stale="true"]` for E2E and CSS.
 * Links the static ingest runbook pointer (`public/runbook.html`). Distinct
 * from the permanent SYNTHETIC DATA banner.
 */
import type { StaleReason } from '@/lib/evaluate-staleness';

import styles from './stale-data-banner.module.css';

import type { JSX } from 'react';

export interface StaleDataBannerProps {
  readonly stale: boolean;
  readonly reasons: readonly StaleReason[];
  /** Override for tests; production points at the static runbook HTML. */
  readonly runbookHref?: string;
}

/** Served from Vite `public/` — real destination until Phase 10 docs/runbook.md. */
export const DEFAULT_RUNBOOK_HREF = '/runbook.html';

const REASON_COPY: Readonly<Record<StaleReason, string>> = {
  frozen_data_advancement: 'Data may be frozen — market data has not advanced.',
  stale_data_as_of: 'Market date is behind the latest operational trading day.',
  stale_finished_at: 'Last successful ingest is older than 36 hours.',
  stale_membership: 'Index membership is older than 7 days.',
  freshness_unevaluable: 'Data freshness could not be verified — a published value is unreadable.',
};

/**
 * Display priority when multiple §6.7 triggers fire.
 *
 * `freshness_unevaluable` is LAST on purpose: when a real trigger also fired
 * we know something concrete and actionable, and leading with "could not
 * verify" would bury it. It surfaces as the headline only when it is the sole
 * signal — which is exactly the case that used to render as healthy.
 */
const REASON_PRIORITY: readonly StaleReason[] = [
  'frozen_data_advancement',
  'stale_data_as_of',
  'stale_finished_at',
  'stale_membership',
  'freshness_unevaluable',
];

/**
 * Accessible name for the live region. Deliberately NOT "Data stale": the
 * region is announced in both states and its own name must not duplicate the
 * DATA STALE text it wraps, or the reading becomes "Data stale, Data stale, …".
 */
const REGION_LABEL = 'Data freshness';

/** `head` is passed in already resolved, so no unreachable empty-list branch. */
function reasonSummary(ordered: readonly StaleReason[], head: StaleReason): string {
  const primary = REASON_COPY[head];
  const extra = ordered.length - 1;
  return extra > 0 ? `${primary} (+${String(extra)} more)` : primary;
}

export function StaleDataBanner({
  stale,
  reasons,
  runbookHref = DEFAULT_RUNBOOK_HREF,
}: StaleDataBannerProps): JSX.Element {
  const ordered = stale ? REASON_PRIORITY.filter((r) => reasons.includes(r)) : [];
  const head = ordered[0];

  /*
   * THE LIVE REGION IS ALWAYS MOUNTED, INCLUDING WHEN THE DATA IS FRESH.
   *
   * A region inserted into the DOM in the same commit as its text is not
   * reliably announced — the assistive technology has to be subscribed to the
   * node before the text arrives. Staleness is a runtime flip (meta resolves,
   * or `use-now-tick` crosses a §6.7 boundary 60s into the session), so
   * mounting on demand would silently drop the one announcement this banner
   * exists to make. Fresh renders a named, empty, visually-hidden region and
   * deliberately carries NO `data-stale` attribute, so the E2E affordance and
   * the CSS hook still mean "stale" and nothing else.
   */
  if (head === undefined) {
    return (
      <div className="visually-hidden" role="status" aria-live="polite" aria-label={REGION_LABEL} />
    );
  }

  return (
    <div
      className={styles['banner']}
      role="status"
      aria-live="polite"
      aria-label={REGION_LABEL}
      data-stale="true"
    >
      <span className={styles['label']}>Data stale</span>
      <span className={styles['detail']}>{reasonSummary(ordered, head)}</span>
      <a className={styles['link']} href={runbookHref}>
        Runbook
        {/* The runbook is a static document OUTSIDE the SPA, so following this
            leaves the current route behind. Say so for anyone who cannot see
            that the destination is a different kind of page. */}
        <span className="visually-hidden"> (opens the ingest runbook page)</span>
      </a>
    </div>
  );
}
